import React, { useState } from "react";
import { TrendingDown, X, RefreshCw, Sparkles } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useEntitlementsSnapshot, useEntitlementsRefresh } from "@/context/EntitlementsContext";
import { usePaywall } from "@/context/PaywallContext";
import { api } from "@/lib/api";

// ─────────────────────────────────────────────────────────────────────────────
//  DowngradeNoticeSheet — one-shot educational sheet shown on the first app
//  launch after a paid subscription expired / was downgraded.
//
//  Backend stamps `subscription.downgrade_acknowledged_at` when dismissed so
//  this sheet never reappears for the same transition. Re-subscribing is
//  one tap away via the existing global paywall.
//
//  Mounted once at the app root. Reads `downgrade_notice` from the
//  entitlements snapshot (Batch 7 backend change).
// ─────────────────────────────────────────────────────────────────────────────
export function DowngradeNoticeSheet() {
  const { family } = useAuth();
  const snapshot = useEntitlementsSnapshot();
  const refresh = useEntitlementsRefresh();
  const { openPaywall } = usePaywall();
  const [dismissing, setDismissing] = useState(false);
  // Local suppression: once the user closes the sheet, hide it for the rest
  // of this session even if the backend ack hasn't round-tripped yet.
  const [hidden, setHidden] = useState(false);

  const notice = snapshot?.downgrade_notice;
  if (!notice || hidden || !family?.id) return null;

  const dismiss = async () => {
    setHidden(true);
    setDismissing(true);
    try {
      await api.post("/billing/acknowledge-downgrade", { family_id: family.id });
      await refresh();
    } catch { /* swallow — sheet stays hidden for this session anyway */ }
    finally {
      setDismissing(false);
    }
  };

  const reSubscribe = async () => {
    setHidden(true);
    // Acknowledge before re-subscribing so it doesn't pop again on success.
    try {
      await api.post("/billing/acknowledge-downgrade", { family_id: family.id });
    } catch { /* non-fatal */ }
    // Target the tier they USED to be on, so the paywall pitches the
    // matching plan first. Falls back to Spark if the prior tier no
    // longer exists (e.g. renamed).
    openPaywall({
      featureKey: "resubscribe",
      requiredTier: notice.from_tier,
      message: `Re-activate ${notice.from_tier_label} to get all your features back. Your data is still here — nothing was deleted.`,
    });
  };

  return (
    <div
      data-testid="downgrade-notice-sheet"
      className="fixed inset-0 z-[63] flex items-end justify-center bg-foreground/40 backdrop-blur-sm"
      onClick={dismiss}
    >
      <div
        className="w-full max-w-[480px] bg-background rounded-t-3xl pb-safe"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-center justify-end px-2 pt-2">
          <button
            onClick={dismiss}
            data-testid="downgrade-notice-close"
            disabled={dismissing}
            className="w-9 h-9 flex items-center justify-center text-muted-foreground tap"
            aria-label="Dismiss"
          >
            <X className="w-5 h-5" />
          </button>
        </header>

        <div className="px-6 pb-8 text-center">
          <div className="w-14 h-14 rounded-full bg-amber-500/15 text-amber-500 mx-auto mb-5 flex items-center justify-center">
            <TrendingDown className="w-7 h-7" />
          </div>

          <h2 className="font-display text-[22px] font-medium tracking-tight" data-testid="downgrade-notice-title">
            Your {notice.from_tier_label} plan ended.
          </h2>
          <p className="text-sm text-muted-foreground mt-2 leading-relaxed">
            You&rsquo;re now on <span className="font-display text-foreground">Kinly {notice.to_tier_label}</span>. Some features will be locked, but everything you&rsquo;ve added is still here — nothing gets deleted.
          </p>

          <div className="mt-6 rounded-2xl border border-border bg-card p-4 text-left text-[13px] text-foreground/85 leading-snug">
            <p className="text-[11px] uppercase tracking-[0.18em] text-muted-foreground font-medium mb-2">
              What changed
            </p>
            <ul className="space-y-1.5">
              <li>• You went from <span className="font-display">{notice.from_tier_label}</span> to <span className="font-display">{notice.to_tier_label}</span></li>
              <li>• Premium features are paused (not deleted)</li>
              <li>• Re-subscribe anytime to flip them back on</li>
            </ul>
          </div>

          <button
            data-testid="downgrade-notice-resubscribe"
            disabled={dismissing}
            onClick={reSubscribe}
            className="w-full h-12 rounded-2xl bg-primary text-primary-foreground font-medium tap mt-6 inline-flex items-center justify-center gap-2 disabled:opacity-60"
          >
            <RefreshCw className="w-4 h-4" />
            Re-activate {notice.from_tier_label}
          </button>

          <button
            data-testid="downgrade-notice-stay-free"
            disabled={dismissing}
            onClick={dismiss}
            className="mt-3 h-11 w-full text-[13px] text-muted-foreground tap inline-flex items-center justify-center"
          >
            Stay on {notice.to_tier_label} for now
          </button>
        </div>
      </div>
    </div>
  );
}
